import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { RoutingConfig } from '@app/routing/routing.config';
import { TokenService } from '@app/core/services/token/token.service';
import { RolesService } from '@app/core/services/roles/roles.service';
import {
  AccountUserTypes,
  hasBookingCustomerAccount,
  hasMusicianAccountProfile
} from '@app/modules/account-modules/account.model';

@Injectable()

export class GuardAccountService implements CanActivate {
  private GuardRole: Array<AccountUserTypes> = [];

  constructor(
    private router: Router,
    private token: TokenService,
    private rolesService: RolesService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): boolean {
    this.GuardRole = route.data.ROLES;

    /**
     * Checking User Tokens.
     */
    if (!!this.token.accessToken && !!this.token.refreshToken) {
      return true;
    }

    /**
     * Checking User Roles
     */
    if (!this.onGetActivateRoles()) {
      this.onRedirectToPermission(route);
      return true;
    }



    this.onRedirectToAuthorisation(route);
  }

  /**
   * Checking of current User Role from storage.
   */
  onGetActivateRoles(): boolean {
    const roles = this.rolesService.roles;
    const profiles = this.rolesService.accountProfiles;
    return this.GuardRole.some(required => {
      if (required === 'musician') {
        return hasMusicianAccountProfile({ profiles });
      }
      if (required === 'individualBookingCustomer') {
        return hasBookingCustomerAccount({ profiles });
      }
      return roles.some(r => r === required);
    });
  }


  /**
   * Redirect to authorisation page with saving path tree.
   * @param route
   */
  onRedirectToAuthorisation(route: ActivatedRouteSnapshot): void {
    const redirectUrl = route['_routerState']['url'];

    /**
     * Redirect to selected URL.
     */
    this.router.navigateByUrl(
      this.router.createUrlTree(
        [RoutingConfig.Login + '/' + RoutingConfig.SignIn], {
          queryParams: {
            redirectUrl
          }
        }
      )
    ).then();
  }


  /**
   * Redirect to permission page with saving path tree.
   * @param route
   */
  onRedirectToPermission(route: ActivatedRouteSnapshot): void {
    const redirectUrl = route['_routerState']['url'];
    const expansionRole = this.getMissingProfileExpansionRole();

    if (expansionRole) {
      const expansionPath =
        expansionRole === 'musician'
          ? RoutingConfig.Account + '/' + RoutingConfig.AccountMusicianProfile
          : RoutingConfig.Account + '/' + RoutingConfig.AccountBookingIndividualProfile;
      this.router.navigateByUrl(
        this.router.createUrlTree(
          [expansionPath],
          {
            queryParams: {
              redirectUrl
            }
          }
        )
      ).then();
      return;
    }

    /**
     * Redirect to selected URL.
     */
    this.router.navigateByUrl(
      this.router.createUrlTree(
        [RoutingConfig.PageErrors + '/' + RoutingConfig.PagePermission], {
          queryParams: {
            redirectUrl
          }
        }
      )
    ).then();
  }

  /**
   * Profile-extensions are created from Account/User page.
   * If the user reached a role-protected action without that profile,
   * send them to profile expansion flow instead of generic permission page.
   */
  private getMissingProfileExpansionRole(): 'musician' | 'individualBookingCustomer' | null {
    if (this.GuardRole.includes('musician')) {
      return 'musician';
    }
    if (this.GuardRole.includes('individualBookingCustomer')) {
      return 'individualBookingCustomer';
    }
    return null;
  }
}
